import { BrowserRouter as Router, Routes, Route, Navigate, NavLink } from 'react-router-dom'
import { QueryClient, QueryClientProvider, useQuery } from '@tanstack/react-query'
import { Toaster } from 'sonner'
import axios from './lib/axios'
import { useState, useEffect } from 'react'
import { LogOut, User } from 'lucide-react'
import TarifasPage from './pages/TarifasPage'
import ContratosPage from './pages/ContratosPage'
import CondicionesPage from './pages/CondicionesPage'
import LiquidacionesPage from './pages/LiquidacionesPage'
import InformesPage from './pages/InformesPage'
import JobsPage from './pages/JobsPage'
import CanalesPage from './pages/CanalesPage'
import LoginPage from './pages/LoginPage'
import RequireAuth from './components/RequireAuth'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
})

function AppContent() {
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [user, setUser] = useState(null)

  useEffect(() => {
    const token = localStorage.getItem('auth_token')
    const userData = localStorage.getItem('user')
    
    if (token && userData) {
      setIsAuthenticated(true)
      setUser(JSON.parse(userData))
    }
  }, [])

  const handleLoginSuccess = (token, userData) => {
    setIsAuthenticated(true)
    setUser(userData)
    // Asegurar que localStorage esté sincronizado
    localStorage.setItem('auth_token', token)
    localStorage.setItem('user', JSON.stringify(userData))
  }
  
  const handleLogout = () => {
    localStorage.removeItem('auth_token')
    localStorage.removeItem('user')
    setIsAuthenticated(false)
    setUser(null)
    window.location.href = '/'
  }
  
  // Query para obtener estadísticas de liquidaciones
  const { data: stats } = useQuery({
    queryKey: ['liquidaciones-stats'],
    queryFn: async () => {
      const res = await axios.get('/api/liquidaciones/stats');
      return res.data;
    },
    refetchInterval: 30000, // Refrescar cada 30 segundos
    enabled: isAuthenticated, // Solo ejecutar si está autenticado
  });

  return (
    <div className="min-h-screen bg-gray-50">
      <Toaster position="top-right" richColors />
      
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="w-full px-2 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <img 
                src="https://imaginaenergia.com/wp-content/uploads/2020/08/cropped-LOGO-IMAGINA-ok-01-1.png.webp" 
                alt="Imagina Energía" 
                className="h-10"
              />
              <h1 className="text-2xl font-bold text-gray-900">
                Liquidador de Comisiones
              </h1>
            </div>
            
            <div className="flex items-center gap-4">
              {/* Badges de estadísticas */}
              {stats && (
                <>
                  {/* TOTALES */}
                  <div className="flex flex-col gap-1">
                    <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider">Totales</span>
                    <div className="flex items-center gap-1.5 px-2.5 py-1.5 bg-white border border-gray-300 rounded shadow-sm">
                      <span className="text-xs text-gray-600">Total:</span>
                      <span className="font-bold text-sm text-gray-900">{stats.total.toLocaleString()}</span>
                    </div>
                  </div>
                  
                  <div className="h-10 w-px bg-gray-300"></div>
                  
                  {/* LIQUIDACIONES */}
                  <div className="flex flex-col gap-1">
                    <span className="text-[10px] font-semibold text-blue-600 uppercase tracking-wider">Liquidaciones</span>
                    <div className="flex items-center gap-1.5">
                      <div className="flex items-center gap-1 px-2 py-1 bg-amber-50 border border-amber-200 rounded text-xs">
                        <span className="text-amber-700">Pend:</span>
                        <span className="font-bold text-amber-900">{stats.liquidacion_pendiente.toLocaleString()}</span>
                      </div>
                      <div className="flex items-center gap-1 px-2 py-1 bg-green-50 border border-green-200 rounded text-xs">
                        <span className="text-green-700">Calc:</span>
                        <span className="font-bold text-green-900">{stats.liquidacion_calculada.toLocaleString()}</span>
                      </div>
                      <div className="flex items-center gap-1 px-2 py-1 bg-red-50 border border-red-200 rounded text-xs">
                        <span className="text-red-700">Bloq:</span>
                        <span className="font-bold text-red-900">{stats.liquidacion_bloqueada.toLocaleString()}</span>
                      </div>
                      <div className="flex items-center gap-1 px-2 py-1 bg-rose-100 border border-rose-300 rounded text-xs">
                        <span className="text-rose-700">Error:</span>
                        <span className="font-bold text-rose-900">{stats.liquidacion_error.toLocaleString()}</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="h-10 w-px bg-gray-300"></div>
                  
                  {/* DECOMISIONES */}
                  <div className="flex flex-col gap-1">
                    <span className="text-[10px] font-semibold text-purple-600 uppercase tracking-wider">Decomisiones</span>
                    <div className="flex items-center gap-1.5">
                      <div className="flex items-center gap-1 px-2 py-1 bg-amber-50 border border-amber-200 rounded text-xs">
                        <span className="text-amber-700">Pend:</span>
                        <span className="font-bold text-amber-900">{stats.decomision_pendiente.toLocaleString()}</span>
                      </div>
                      <div className="flex items-center gap-1 px-2 py-1 bg-green-50 border border-green-200 rounded text-xs">
                        <span className="text-green-700">Calc:</span>
                        <span className="font-bold text-green-900">{stats.decomision_calculada.toLocaleString()}</span>
                      </div>
                      <div className="flex items-center gap-1 px-2 py-1 bg-red-50 border border-red-200 rounded text-xs">
                        <span className="text-red-700">Bloq:</span>
                        <span className="font-bold text-red-900">{stats.decomision_bloqueada.toLocaleString()}</span>
                      </div>
                      <div className="flex items-center gap-1 px-2 py-1 bg-rose-100 border border-rose-300 rounded text-xs">
                        <span className="text-rose-700">Error:</span>
                        <span className="font-bold text-rose-900">{stats.decomision_error.toLocaleString()}</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="h-10 w-px bg-gray-300"></div>
                  
                  {/* ACTIVACIÓN FUTURA */}
                  <div className="flex flex-col gap-1">
                    <span className="text-[10px] font-semibold text-indigo-600 uppercase tracking-wider">Activ. Futura</span>
                    <div className="flex items-center gap-1 px-2 py-1 bg-indigo-50 border border-indigo-200 rounded text-xs">
                      <span className="font-bold text-indigo-900">{stats.activacion_futura.toLocaleString()}</span>
                    </div>
                  </div>
                  
                  <div className="h-10 w-px bg-gray-300"></div>
                </>
              )}
              
              {/* Info de usuario y logout */}
              {isAuthenticated && user && (
                <>
                  <div className="flex items-center gap-2 px-3 py-1.5 bg-gray-50 rounded-lg">
                    <User className="w-4 h-4 text-gray-600" />
                    <span className="text-sm font-medium text-gray-700">
                      {user.name || user.email}
                    </span>
                  </div>
                  
                  <button
                    onClick={handleLogout}
                    className="flex items-center gap-2 px-3 py-1.5 bg-red-50 hover:bg-red-100 text-red-700 rounded-lg transition-colors"
                    title="Cerrar sesión"
                  >
                    <LogOut className="w-4 h-4" />
                    <span className="text-sm font-medium">Salir</span>
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Navigation */}
      <nav className="bg-white border-b border-gray-200">
        <div className="w-full px-2">
          <div className="flex items-center justify-between">
          <div className="flex space-x-8">
            <NavLink 
              to="/tarifas" 
              className={({ isActive }) => 
                `py-4 px-3 border-b-2 font-medium transition-colors ${
                  isActive 
                    ? 'border-primary-500 text-primary-600' 
                    : 'border-transparent hover:border-primary-500 text-gray-700 hover:text-gray-900'
                }`
              }
            >
              Tarifas
            </NavLink>
            <NavLink 
              to="/contratos" 
              className={({ isActive }) => 
                `py-4 px-3 border-b-2 font-medium transition-colors ${
                  isActive 
                    ? 'border-primary-500 text-primary-600' 
                    : 'border-transparent hover:border-primary-500 text-gray-700 hover:text-gray-900'
                }`
              }
            >
              Contratos
            </NavLink>
            <NavLink 
              to="/liquidaciones" 
              className={({ isActive }) => 
                `py-4 px-3 border-b-2 font-medium transition-colors ${
                  isActive 
                    ? 'border-primary-500 text-primary-600' 
                    : 'border-transparent hover:border-primary-500 text-gray-700 hover:text-gray-900'
                }`
              }
            >
              Liquidaciones
            </NavLink>
            <NavLink 
              to="/canales" 
              className={({ isActive }) => 
                `py-4 px-3 border-b-2 font-medium transition-colors ${
                  isActive 
                    ? 'border-primary-500 text-primary-600' 
                    : 'border-transparent hover:border-primary-500 text-gray-700 hover:text-gray-900'
                }`
              }
            >
              Canales
            </NavLink>
            <NavLink 
              to="/informes" 
              className={({ isActive }) => 
                `py-4 px-3 border-b-2 font-medium transition-colors ${
                  isActive 
                    ? 'border-primary-500 text-primary-600' 
                    : 'border-transparent hover:border-primary-500 text-gray-700 hover:text-gray-900'
                }`
              }
            >
              Informes
            </NavLink>
            <NavLink 
              to="/jobs" 
              className={({ isActive }) => 
                `py-4 px-3 border-b-2 font-medium transition-colors ${
                  isActive 
                    ? 'border-primary-500 text-primary-600' 
                    : 'border-transparent hover:border-primary-500 text-gray-700 hover:text-gray-900'
                }`
              }
            >
              Procesos
            </NavLink>
          </div>

          {/* Punto de anclaje para que las páginas inserten sus botones de acción a la derecha del submenú */}
          <div id="nav-actions-slot" className="flex items-center gap-1.5"></div>
          </div>
        </div>
      </nav>

      {/* Main content */}
      <Routes>
        {/* Ruta pública de login */}
        <Route path="/login" element={
          isAuthenticated ? <Navigate to="/tarifas" replace /> : <LoginPage onLoginSuccess={handleLoginSuccess} />
        } />
        
        {/* Rutas protegidas */}
        <Route path="/" element={
          <RequireAuth>
            <Navigate to="/tarifas" replace />
          </RequireAuth>
        } />
        <Route path="/tarifas" element={
          <RequireAuth>
            <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
              <TarifasPage />
            </main>
          </RequireAuth>
        } />
        <Route path="/contratos" element={
          <RequireAuth>
            <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
              <ContratosPage />
            </main>
          </RequireAuth>
        } />
        <Route path="/contratos/:contratoId/condiciones" element={
          <RequireAuth>
            <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
              <CondicionesPage />
            </main>
          </RequireAuth>
        } />
        <Route path="/liquidaciones" element={
          <RequireAuth>
            <main className="w-full h-[calc(100vh-144px)]">
              <LiquidacionesPage />
            </main>
          </RequireAuth>
        } />
        <Route path="/canales" element={
          <RequireAuth>
            <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
              <CanalesPage />
            </main>
          </RequireAuth>
        } />
        <Route path="/informes" element={
          <RequireAuth>
            <main className="w-full">
              <InformesPage />
            </main>
          </RequireAuth>
        } />
        <Route path="/jobs" element={
          <RequireAuth>
            <main className="w-full">
              <JobsPage />
            </main>
          </RequireAuth>
        } />
      </Routes>
    </div>
  )
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <Router>
        <AppContent />
      </Router>
    </QueryClientProvider>
  )
}

export default App
